// REDACTION 4 — two honest phases.
//
// The handoff prototype simulated a 5-phase agent dispatch
// (Coordinator → agent pair → IFPE → market scan → synthesis).
// v2.1.1 ruling: never expose internal agent architecture in any
// user-facing surface. Two-phase progress with honest copy.
//
// SR-WIRE — when `repairedAt` becomes non-null the parent advances to
// the results screen. The page polls the session every 1.5s while this
// component is mounted (parent owns the polling cadence).
import { useEffect, useState, type ReactElement } from "react";
import { SrIcon } from "./SrIcon.tsx";
import { Stepper } from "./Stepper.tsx";

interface Props {
  marketsScanned: number;
  onDone?: () => void;
  onTimeout?: () => void;
  // SR-WIRE — when set, the screen treats this as the source of truth
  // and calls onDone once it transitions to non-null. The stub timer
  // path is only used when this prop is undefined (DEV stub mode).
  repairedAt?: string | null;
}

const PHASES: { label: string; ms: number }[] = [
  { label: "Scanning substrate for", ms: 1500 },
  { label: "Writing your repair report…", ms: 2500 },
];

const TIMEOUT_MS = 90_000;

export function RepairingScreen({ marketsScanned, onDone, onTimeout, repairedAt }: Props): ReactElement {
  const [phase, setPhase] = useState(0);
  const [timedOut, setTimedOut] = useState(false);
  const liveMode = repairedAt !== undefined;

  // Live mode: advance phase visually after a soft delay so the user
  // sees progress while the backend runs; then watch repairedAt.
  useEffect(() => {
    if (!liveMode) return;
    if (repairedAt) {
      onDone?.();
      return;
    }
    if (phase === 0) {
      const t = setTimeout(() => setPhase(1), 2000);
      return () => clearTimeout(t);
    }
  }, [liveMode, repairedAt, phase, onDone]);

  // Timeout fallback: if repaired_at is never set, surface a retry
  // option instead of spinning forever.
  useEffect(() => {
    if (!liveMode || repairedAt || timedOut) return;
    const t = setTimeout(() => setTimedOut(true), TIMEOUT_MS);
    return () => clearTimeout(t);
  }, [liveMode, repairedAt, timedOut]);

  // Stub mode: timer-driven progression (legacy DEV path).
  useEffect(() => {
    if (liveMode) return;
    if (phase >= PHASES.length) {
      onDone?.();
      return;
    }
    const t = setTimeout(() => setPhase((p) => p + 1), PHASES[phase]!.ms);
    return () => clearTimeout(t);
  }, [liveMode, phase, onDone]);

  const totalMs = PHASES.reduce((a, p) => a + p.ms, 0);
  const elapsedMs = PHASES.slice(0, phase).reduce((a, p) => a + p.ms, 0);
  const pct = Math.min(100, Math.round((elapsedMs / totalMs) * 100) + (phase < PHASES.length ? 10 : 0));

  const phaseLabel =
    phase === 0
      ? `Scanning substrate for ${marketsScanned.toLocaleString()} markets…`
      : phase === 1
        ? "Writing your repair report…"
        : "Ready.";

  return (
    <div className="sr-page" style={{ maxWidth: 720 }}>
      <Stepper active="repair" />
      <div className="sr-hero" style={{ padding: 40, textAlign: "center" }}>
        <div
          style={{
            margin: "0 auto 18px",
            width: 56,
            height: 56,
            borderRadius: 14,
            background: timedOut ? "var(--neg-soft, rgba(214,40,40,0.12))" : "var(--accent-soft)",
            color: timedOut ? "var(--neg, #d62828)" : "var(--accent-text)",
            display: "grid",
            placeItems: "center",
          }}
        >
          <SrIcon name={timedOut ? "info" : "wrench"} size={26} />
        </div>
        {timedOut ? (
          <>
            <h2 style={{ margin: "0 0 8px", fontSize: 20, fontWeight: 600 }}>Taking longer than expected</h2>
            <p style={{ margin: "0 0 20px", color: "var(--text-tertiary)", fontSize: 13 }}>
              The repair engine hasn't responded. You can retry or go back and start a new session.
            </p>
            <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
              <button
                className="btn"
                onClick={() => {
                  setTimedOut(false);
                  setPhase(0);
                  onTimeout?.();
                }}
              >
                Back to configure
              </button>
            </div>
          </>
        ) : (
          <>
            <h2 style={{ margin: "0 0 8px", fontSize: 20, fontWeight: 600 }}>Repairing your slip…</h2>
            <p style={{ margin: 0, color: "var(--text-tertiary)", fontSize: 13 }}>{phaseLabel}</p>
            <div
              style={{
                marginTop: 24,
                height: 4,
                background: "var(--bg-inset)",
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  height: "100%",
                  width: `${pct}%`,
                  background: "var(--accent)",
                  transition: "width .4s",
                }}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
